/**
 * 解析 TikTok Create Product / publish 响应。
 * 官方成功体：{ code:0, data:{ product_id, skus:[{ id, seller_sku, sales_attributes }] } }
 * 无 SKC / SPU 概念；本地 publishedSpu / platformSpuName 存的是 product_id。
 */

/** 取出 Create Product 的 data（兼容 job.response / 解包体） */
export function unwrapTiktokCreateProductData(res) {
  if (!res || typeof res !== 'object') return null;
  if (res.data && typeof res.data === 'object' && (res.data.product_id != null || res.data.productId != null || Array.isArray(res.data.skus))) {
    return res.data;
  }
  if (res.response?.data && typeof res.response.data === 'object') {
    return res.response.data;
  }
  if (res.result && typeof res.result === 'object') return res.result;
  if (res.product_id != null || res.productId != null || Array.isArray(res.skus)) return res;
  return null;
}

/** Create Product 返回的 SKU 列表（id 保持字符串，雪花 ID 超出 Number 安全整数） */
export function extractTiktokPublishedSkus(res) {
  const data = unwrapTiktokCreateProductData(res);
  const skus = data?.skus;
  return Array.isArray(skus) ? skus : [];
}

function collectValidationMessages(res) {
  const info = res?.info && typeof res.info === 'object' ? res.info : res;
  if (!info || typeof info !== 'object') return [];
  const buckets = [
    info.pre_valid_result,
    info.mcc_valid_result,
    info.preValidResult,
    info.mccValidResult,
  ];
  const out = [];
  buckets.forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((row) => {
      const messages = row?.messages;
      const extras = [];
      const code = String(row?.code || row?.error_code || row?.errorCode || '').trim();
      const field = String(row?.field || row?.field_name || row?.fieldName || '').trim();
      if (code || field) extras.push(`[code=${code || '-'} field=${field || '-'}]`);

      if (Array.isArray(messages)) {
        messages.forEach((m) => {
          const text = m == null ? '' : String(m).trim();
          if (!text) return;
          const formName = String(row?.form_name || row?.formName || '').trim();
          out.push(formName ? `${formName}：${text}` : text);
        });
        extras.forEach((e) => out.push(e));
        return;
      }
      const single = String(row?.message || row?.msg || '').trim();
      if (single) {
        const formName = String(row?.form_name || row?.formName || '').trim();
        out.push(formName ? `${formName}：${single}` : single);
      }
      extras.forEach((e) => out.push(e));
    });
  });
  return out;
}

export function isTiktokPublishSuccess(res) {
  if (!res || typeof res !== 'object') return false;
  if (res.ok === true) return true;
  if (res.publishedSpu || res.product_id || res.productId) return true;
  // 后端 requireResult 已解包：response 顶层即 { productId, productSkcList, ... }
  if (res.productId != null || res.productSkcList != null || res.productSkuList != null) {
    return true;
  }
  if (res.result && (
    res.result.productId != null
    || res.result.product_id != null
    || res.result.productSkcList != null
    || res.result.productSkuList != null
  )) {
    return true;
  }
  if (res.data && (
    res.data.product_id != null
    || res.data.productId != null
  )) {
    return true;
  }
  if (res.response?.data && (
    res.response.data.product_id != null
    || res.response.data.productId != null
  )) {
    return true;
  }
  if (res.info && typeof res.info === 'object' && Object.prototype.hasOwnProperty.call(res.info, 'success')) {
    return res.info.success === true;
  }
  if (typeof res.success === 'boolean') {
    return res.success === true;
  }
  const errorCode = res.errorCode ?? res.error_code;
  if (String(errorCode) === '1000000') return true;
  const code = res.code == null ? '' : String(res.code);
  return code === '0' || code === '000000';
}

export function extractTiktokPublishFailMessage(res) {
  const validation = collectValidationMessages(res);
  if (validation.length) {
    return validation.slice(0, 8).join('；');
  }
  const detail = [
    res?.msg,
    res?.message,
    res?.errorMsg,
    res?.error_msg,
    res?.info?.msg,
    res?.info?.message,
  ]
    .map((v) => (v == null ? '' : String(v).trim()))
    .find((v) => v && v.toUpperCase() !== 'OK');
  return detail || '发布未成功，平台未返回货品 ID';
}

/**
 * 提取平台商品 ID（Create Product.data.product_id）。
 * 参数名仍带 SPU 是历史兼容；TTS 无 SPU，返回值即 product_id。
 */
export function extractTiktokPublishedSpu(res, payload = {}, formSpuName = '') {
  const data = unwrapTiktokCreateProductData(res);
  return String(
    res?.publishedSpu
    || data?.product_id
    || data?.productId
    || res?.result?.productId
    || res?.result?.product_id
    || res?.data?.product_id
    || res?.data?.productId
    || res?.response?.data?.product_id
    || res?.response?.data?.productId
    || res?.info?.spu_name
    || res?.info?.spuName
    || res?.spu_name
    || res?.spuName
    || res?.info?.data?.spu_name
    || res?.info?.data?.spuName
    || res?.productId
    || res?.product_id
    || payload?.spu_name
    || payload?.spuName
    || payload?.productName
    || formSpuName
    || ''
  ).trim();
}
